// bind/call/apply 로 BroadcastBox(this)에 바인딩되는 채팅/도네이션 매처.
//  this._keywordRules  : [{ id, isMentionMode, keywords[], reactionId, isEnabled, label }] (DB 계층)
//  this._sessionRules  : 위와 같은 shape, rnd 생성 SESSION 휘발 레이어(매칭 시 union)
//  this._donationRules : [{ id, minAmount, maxAmount, reactionId }]
//  this._donationEnabled: boolean
//  this._reactionById / this._reactionBySlug : reaction 해석 맵

import { pickFallbackReactionId } from "@/domain/reaction/MentionModeFallbackReactionHandler";
import { isLocal } from "@/utils/env-utils";
import { isInteger } from "lodash";

function postExecute(expNumber) {
  if (this.increaseExp) this.increaseExp(expNumber);
  if (this._sleepCounter) this._sleepCounter.reset();
}

// text에 매칭되는 keyword 하나 반환(대소문자 무시). 없으면 null.
function matchedKeyword(text, keywords) {
  const t = (text || "").toLowerCase();
  return (keywords || []).find((k) => k && t.includes(k.toLowerCase())) || null;
}

// rules 중 text에 매칭되는 것들 모아 랜덤 1개 선택. 반환 { reactionId, keyword } | null.
//  - reactionId 없거나(미지정) 해석 불가(dangling)면 후보 제외.
function pickRandomMatch(rules, text, reactionById) {
  const matches = [];
  for (const r of rules) {
    if (!r.reactionId || !reactionById[r.reactionId]) continue;
    const keyword = matchedKeyword(text, r.keywords);
    if (keyword != null) matches.push({ reactionId: r.reactionId, keyword });
  }
  if (!matches.length) return null;
  return matches[Math.floor(Math.random() * matches.length)];
}

// 플로우 A(엄격 분기): @펫이름이면 멘션 경로(멘션룰 or fallback), 아니면 일반 경로.
export function chatHandler(context = {}) {
  try {
    const { virtupetProfile, chat } = context;
    const { message, hidden } = chat || {};
    if (!message || hidden) return;

    // "ㅋ" 포함 시 파워 이펙트 1회(개수 무관, 리액션 매칭과 독립). 비활성이면 내부에서 무시.
    if (message.includes("ㅋ") && this.triggerEffect) this.triggerEffect();

    const byId = this._reactionById || {};
    // DB 계층(_keywordRules) + SESSION 휘발 레이어(_sessionRules)를 한 풀로 union해 매칭.
    const rules = [...(this._keywordRules || []), ...(this._sessionRules || [])].filter((r) => r.isEnabled);
    const at = `@${virtupetProfile.name}`;

    // ── 멘션 경로 ──
    if (message.startsWith(at)) {
      const rest = message.slice(at.length).trim();
      const picked = pickRandomMatch(
        rules.filter((r) => r.isMentionMode),
        rest,
        byId,
      );
      if (picked) {
        context.keyword = picked.keyword;
        this.executeInstruction(picked.reactionId, context);
        return postExecute.call(this, 2);
      }
      // 매칭 없음 → fallback(랜덤 시스템 리액션)
      const fbId = pickFallbackReactionId(this._reactionBySlug);
      if (fbId) {
        this.executeInstruction(fbId, context);
        return postExecute.call(this, 1);
      }
      return;
    }

    // ── 일반 경로 ──
    const picked = pickRandomMatch(
      rules.filter((r) => !r.isMentionMode),
      message,
      byId,
    );
    if (picked) {
      context.keyword = picked.keyword;
      this.executeInstruction(picked.reactionId, context);
      return postExecute.call(this, 1);
    }
  } catch (error) {
    console.error("E_CHAT_HANDLER");
    console.error(error);
  }
}

export function donationHandler(context = {}) {
  try {
    if (!this._donationEnabled) return;

    const { donation } = context;
    const payAmount = (donation.extras || {}).payAmount;
    const byId = this._reactionById || {};

    const hit = (this._donationRules || []).find((r) => {
      const max = r.maxAmount == null ? Infinity : r.maxAmount;
      return payAmount >= r.minAmount && payAmount <= max;
    });

    if (!hit || !hit.reactionId || !byId[hit.reactionId]) {
      return isLocal() && console.log("No donation reaction");
    }

    this.executeInstruction(hit.reactionId, context);

    const exp = parseInt(payAmount / 10);
    if (isInteger(exp)) this.increaseExp(exp);

    try {
      this._sleepCounter.reset();
    } catch (error) {
      /* noop */
    }
  } catch (error) {
    console.error("E_DONATION_HANDLER");
    console.error(error);
  }
}
